import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, Heart, Loader2 } from "lucide-react";
import PublicShell, { WIDE } from "@/components/PublicShell";
import { api } from "@/lib/apiClient";
import { usePageMeta } from "@/lib/seo";
import { SIGN_FACTS, cap } from "@/lib/signFacts";
import { useAuth } from "@/context/AuthContext";
import PairShareCard from "@/components/PairShareCard";

export const ORDER = Object.keys(SIGN_FACTS);
export const pairSlug = (a, b) => (ORDER.indexOf(a) <= ORDER.indexOf(b) ? `${a}-${b}` : `${b}-${a}`);

function Facts({ a, b }) {
  const fa = SIGN_FACTS[a], fb = SIGN_FACTS[b];
  const rows = [["Dates", fa.dates, fb.dates], ["Element", fa.element, fb.element], ["Mode", fa.modality, fb.modality], ["Ruler", fa.ruler, fb.ruler]];
  return (
    <table data-testid="compat-facts" className="w-full text-sm mb-6 bg-[#0F1523]/90 border border-[#D4AF37]/20 rounded-2xl overflow-hidden">
      <thead><tr className="text-left"><th className="p-3 font-normal text-[#A7B0C0]" /><th className="p-3 font-normal gold-text">{cap(a)}</th><th className="p-3 font-normal gold-text">{cap(b)}</th></tr></thead>
      <tbody>{rows.map(([k, x, y]) => <tr key={k} className="border-t border-[#D4AF37]/10"><td className="p-3 text-[#A7B0C0]">{k}</td><td className="p-3">{x}</td><td className="p-3">{y}</td></tr>)}</tbody>
    </table>
  );
}

function MorePairs({ a, b }) {
  return (
    <nav aria-label={`More ${cap(a)} compatibility`} data-testid="compat-more" className="mt-8">
      <h2 className="text-lg font-light mb-3">More {cap(a)} compatibility</h2>
      <div className="flex flex-wrap gap-2">
        {ORDER.filter((s) => s !== b).map((s) => (
          <Link key={s} to={`/compatibility/${pairSlug(a, s)}`} data-testid={`compat-more-${s}`} className="text-sm border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 text-[#E8DCC4] rounded-full px-3 py-1.5 transition-colors">{cap(a)} × {cap(s)}</Link>
        ))}
        <Link to="/compatibility" data-testid="compat-all" className="text-sm gold-text px-2 py-1.5">All pairs</Link>
      </div>
    </nav>
  );
}

export default function CompatibilityPage() {
  const { user } = useAuth();
  const { pair = "" } = useParams();
  const [x, y] = pair.toLowerCase().split("-");
  const valid = SIGN_FACTS[x] && SIGN_FACTS[y] && pair.split("-").length === 2;
  const canonical = valid ? pairSlug(x, y) : null;
  const [a, b] = canonical ? canonical.split("-") : [];
  const [d, setD] = useState(null);
  const [error, setError] = useState(false);
  const title = valid ? `${cap(a)} and ${cap(b)} Compatibility: Love, Friendship & Chemistry · Astra` : "Compatibility · Astra";
  usePageMeta(title, `/compatibility/${canonical || pair}`, valid && `How compatible are ${cap(a)} and ${cap(b)}? Chemistry, communication, strengths and friction points for this ${SIGN_FACTS[a].element.toLowerCase()}–${SIGN_FACTS[b].element.toLowerCase()} pairing.`);
  useEffect(() => {
    if (!canonical || canonical !== pair.toLowerCase()) return;
    setD(null); setError(false);
    api.get(`/public/compatibility/${canonical}`).then(({ data }) => setD(data)).catch(() => setError(true));
  }, [canonical, pair]);

  if (!valid) return <PublicShell testId="compat-page"><p data-testid="compat-not-found" className="text-center text-[#A7B0C0] py-24">This sign pair doesn't exist. <Link to="/compatibility" className="gold-text">See all pairs</Link></p></PublicShell>;
  if (canonical !== pair) return <Navigate replace to={`/compatibility/${canonical}`} />;
  return (
    <PublicShell testId="compat-page">
      <article className={`px-5 pt-6 pb-10 fade-up ${WIDE ? "lg:max-w-3xl lg:mx-auto lg:pt-12" : ""}`}>
        <p className="text-[11px] uppercase tracking-[0.3em] gold-text"><Heart size={11} className="inline -mt-0.5 mr-1" />Sign compatibility</p>
        <h1 data-testid="compat-title" className="text-4xl sm:text-5xl font-light mt-2 mb-6">{cap(a)} and {cap(b)} compatibility</h1>
        <Facts a={a} b={b} />
        {error && <p data-testid="compat-error" className="text-[#A7B0C0]">This guide is taking a moment to load. Please refresh in a minute.</p>}
        {!d && !error && <div className="flex justify-center py-12"><Loader2 className="animate-spin gold-text" size={26} /></div>}
        {d && <section data-testid="compat-text">{d.text.split(/\n+/).filter(Boolean).map((p, i) => <p key={i} className="text-base leading-relaxed text-[#D6D0C5] mb-4">{p}</p>)}</section>}
        {d && user && <PairShareCard a={a} b={b} text={d.text} />}
        <p className="text-xs text-[#A7B0C0] mt-2">Sun-sign compatibility is a starting point. Your Moon, Venus and rising signs tell the fuller story. <Link to="/editorial-policy" className="gold-text">How we create content</Link></p>
        <Link to="/readings/synastry" data-testid="compat-cta" className="mt-6 flex items-center justify-between bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium rounded-full px-6 py-3.5 transition-colors">
          Compare your two full birth charts <ArrowRight size={17} />
        </Link>
        <div className="mt-4 flex gap-3 text-sm">
          <Link to={`/s/${a}/daily`} className="gold-text" data-testid="compat-sign-a">{cap(a)} horoscope today</Link>
          {a !== b && <Link to={`/s/${b}/daily`} className="gold-text" data-testid="compat-sign-b">{cap(b)} horoscope today</Link>}
        </div>
        <MorePairs a={a} b={b} />
      </article>
    </PublicShell>
  );
}
